import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const SeparatelyVsNonSeparately = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Separately vs Nonseparately Stated Items | Form 1120-S Guide"
          description="Learn the difference between separately and nonseparately stated items in an S corporation, how they appear on Schedule K-1, and their impact on Form 1120-S"
          keywords=""
          canonical="resources/business-tax/form-1120-s-separately-vs-nonseparately-stated-items/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Separately vs. Nonseparately</span>
          </span>{' '}
          <span style={{ position: 'relative', zIndex: 9 }}>Stated Items</span>{' '}
          <Hidden smDown>
            <br />
          </Hidden>
          in an S Corporation
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/form-1120-s-separately-vs-nonseparately-stated-items/"
                  />
                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Separately Stated vs Nonseparately Stated Items at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Understand the difference between separately stated
                          and nonseparately stated items
                        </strong>{' '}
                        in an S corporation and how each category affects
                        shareholder reporting through Schedule K-1 (Form
                        1120-S).
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Explore why the IRS requires certain items to be
                          separately stated,
                        </strong>{' '}
                        including capital gains, charitable contributions,
                        credits, tax-exempt income, and other items that may
                        have different tax treatments for shareholders.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Understand how separately stated and nonseparately
                          stated items flow through Schedule K-1,
                        </strong>{' '}
                        and how shareholders use this information when preparing
                        their individual tax returns.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Avoid common S corporation reporting mistakes
                        </strong>{' '}
                        by learning how these items impact shareholder basis,
                        tax limitations, deduction rules, and accurate Form
                        1120-S compliance.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When an S corporation prepares its annual return, income and
                  deductions are divided into two primary categories:{' '}
                  <span style={{ fontWeight: 600 }}>
                    separately stated items
                  </span>{' '}
                  and{' '}
                  <span style={{ fontWeight: 600 }}>
                    nonseparately stated items
                  </span>
                  . Understanding this distinction is essential because each
                  category affects how shareholders report pass-through activity
                  from{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f1120ssk.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Schedule K-1
                  </Link>
                  .
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  According to IRS guidance in the{' '}
                  <Link
                    href="https://www.irs.gov/instructions/i1120ssk"
                    target="_blank"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Schedule K-1 (Form 1120-S) shareholder instructions
                  </Link>
                  , certain items must be disclosed separately so that
                  shareholders can apply individual tax rules, limitations, and
                  rates when preparing their personal returns.
                </Typography>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#nonsep" sx={{ color: '#0202ff' }}>
                          What Are Nonseparately Stated Items?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sep" sx={{ color: '#0202ff' }}>
                          What Are Separately Stated Items?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Differences at a Glance
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why the IRS Separates These Categories
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How These Items Appear on Schedule K-1
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#impact" sx={{ color: '#0202ff' }}>
                          Impact on Shareholder Basis and Tax Reporting
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Mistakes to Avoid
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#distinction" sx={{ color: '#0202ff' }}>
                          Why This Distinction Matters for Form 1120-S Filers
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="nonsep"
                >
                  What Are Nonseparately Stated Items?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Nonseparately stated items represent the core operating
                  results of the business. These are combined into a single
                  figure called{' '}
                  <span style={{ fontWeight: 600 }}>
                    ordinary business income or loss
                  </span>
                  , which flows through to shareholders.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Examples include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Revenue from normal business operations</li>
                  <li>Routine operating expenses</li>
                  <li>Wages, rent, and general overhead</li>
                  <li>Cost of goods sold and standard deductions</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Because these items receive the same tax treatment for all
                  shareholders, they are grouped together rather than listed
                  individually.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sep"
                >
                  What Are Separately Stated Items?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Separately stated items are reported individually because
                  their tax treatment may vary depending on each shareholder’s
                  personal tax situation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Common examples include:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Capital gains and losses</li>
                  <li>Interest or dividend income</li>
                  <li>Charitable contributions</li>
                  <li>Section 1231 gains and losses</li>
                  <li>Tax-exempt income</li>
                  <li>Certain credits and adjustments</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The IRS requires separate reporting so that shareholders can
                  apply the correct tax rates, deduction limits, and eligibility
                  rules.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Key Differences at a Glance
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 200,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Feature
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 200,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Separately Stated Items
                          </TableCell>
                          <TableCell sx={{ width: 200, fontWeight: 600 }}>
                            Nonseparately Stated Items
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Reporting Method
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Listed individually on Schedule K-1
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Combined into ordinary income/loss
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Tax Treatment
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Depends on shareholder&apos;s personal tax rules
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Same treatment for all shareholders
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Examples
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Capital gains, credits
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Sales revenue, payroll
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Impact on Basis
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Adjusted individually
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Adjusted as part of ordinary income
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS Purpose
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Preserve tax character
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Simplify reporting of routine activity
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why the IRS Separates These Categories
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The primary reason for separating items is that shareholders
                  may experience different tax outcomes depending on personal
                  circumstances.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For example:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Capital gains may qualify for different tax rates than
                    ordinary income.
                  </li>
                  <li>
                    Charitable contributions may be limited based on adjusted
                    gross income.
                  </li>
                  <li>
                    Investment income may affect additional tax calculations.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  By separating these items, the IRS ensures accurate individual
                  reporting tied to Form 1120-S.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How These Items Appear on Schedule K-1
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  On Schedule K-1:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      Nonseparately stated items
                    </span>{' '}
                    appear as ordinary business income or loss.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>
                      Separately stated items
                    </span>{' '}
                    are listed individually with specific box references.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The Schedule K-1 shows allocated amounts, but shareholders
                  must determine the final tax outcome on their personal return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="impact"
                >
                  Impact on Shareholder Basis and Tax Reporting
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Both categories influence shareholder basis, but in different
                  ways.
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 12 }}>
                    Ordinary income from nonseparately stated items generally
                    increases stock basis as a single combined figure.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Separately stated items may increase or decrease basis
                    depending on the nature of the transaction.
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Mistakes to Avoid
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Shareholders often misunderstand how these categories work,
                  leading to issues such as:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Combining capital gains with ordinary income
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Ignoring separately stated deductions when calculating basis
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Assuming all pass-through income is taxed the same way
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Reviewing Schedule K-1 carefully and following IRS guidance
                  helps avoid these problems.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="distinction"
                >
                  Why This Distinction Matters for Form 1120-S Filers
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Separately and nonseparately stated items ensure that S
                  corporation reporting remains accurate and flexible for
                  shareholders with different tax situations. Understanding the
                  difference helps prevent misreported income and supports
                  better tax planning tied to{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1120-S filings
                  </Link>
                  .
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Form 1120-S with Confidence
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone helps S corporations accurately report separately and
              nonseparately stated items while preparing Form 1120-S and
              generating Schedule K-1s quickly and securely.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=sepvsnonsep`}
              >
                E-file your Form 1120-S
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default SeparatelyVsNonSeparately;
